import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Container,
  Group,
  Skeleton,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconArrowRight,
  IconBuilding,
  IconClipboardCheck,
  IconCoin,
  IconFileText,
  IconInfoCircle,
  IconMapPin,
  IconScale,
  IconWheat,
} from '@tabler/icons-react';
import SectionLabel from '@/components/common/SectionLabel';
import FormErrorsSummary from '@/components/common/FormErrorsSummary';
import DealSummaryCard from '@/components/common/DealSummaryCard';
import TermRow from '@/components/common/TermRow';
import BuyAcceptanceFields from '@/components/app/buy/BuyAcceptanceFields';
import SellerCoreTermsView from '@/components/app/sell/SellerCoreTermsView';
import OrderFileLinks from '@/components/common/OrderFileLinks';
import { buildQualityAcceptance, initQaOverrides, isAcceptanceComplete, type QaOverrides } from '@/lib/buyAcceptance';
import { orderService } from '@/api/services/OrderService';
import { ApiError, getApiMessage, getStatus, isServiceUnavailable } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import { useIsAgent } from '@/hooks/useRoles';
import { decodeEntities, formatOrgName } from '@/lib/format';
import { ROUTES } from '@/routes/paths';
import IdText from '@/components/common/IdText';

// Поллинг Camunda: интервал/лимит. ~60 секунд достаточно (старый портал ждал так же, по 500мс).
const POLL_INTERVAL_MS = 800;
const POLL_DEADLINE_MS = 60_000;

/** Создание не удалось на стороне Camunda (suspended). */
class DealSuspendedError extends Error {}
/** Не дождались bargainId за разумное время (сделка может ещё оформиться). */
class DealTimeoutError extends Error {}

const CREDIT_TYPE_KEYS: Readonly<Record<string, string>> = {
  BY_AVERAGE_VALUE: 'app.buy.terms.creditAverage',
  BY_EACH_DAY: 'app.buy.terms.creditEachDay',
};

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// Превью оффера продажи (Фаза 2b): покупатель смотрит условия продавца и создаёт сделку.
// Создание асинхронное (Camunda): POST → processId → поллинг статуса до bargainId.
export default function OfferPreviewPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const isAgent = useIsAgent();
  const [params] = useSearchParams();

  const saleOrderId = Number(params.get('saleOrderId'));
  const saleOrderVersion = Number(params.get('saleOrderVersion'));
  // Опционален: есть при покупке под свою строку закупки, нет при входе с экрана «Лучшая цена».
  const rawFromPurchaseOrderId = Number(params.get('fromPurchaseOrderId'));
  const fromPurchaseOrderId =
    Number.isFinite(rawFromPurchaseOrderId) && rawFromPurchaseOrderId > 0
      ? rawFromPurchaseOrderId
      : undefined;
  const toAddress = params.get('toAddress');
  const toAddressTitle = params.get('toAddressTitle');
  const goods = params.get('goods');

  const valid =
    Number.isFinite(saleOrderId) &&
    saleOrderId > 0 &&
    Number.isFinite(saleOrderVersion) &&
    !!toAddress &&
    !!toAddressTitle &&
    !!goods;

  // Свободный режим (из поиска «Лучшая цена», без своего прайса) — покупатель сам задаёт приёмку.
  const freeMode = fromPurchaseOrderId == null;
  // Выгода «к вашей цене» приходит из ссылки (на странице её неоткуда взять). В свободном режиме
  // (поиск без привязки) выгоду не показываем — только цену.
  const advParam = params.get('advantage');
  const advantage = !freeMode && advParam != null && advParam !== '' && Number.isFinite(Number(advParam)) ? Number(advParam) : undefined;
  // Моя (целевая) цена — авторитетное значение с бэкенда через ссылку (не реконструируем из лучшей и
  // выгоды). В свободном режиме отсутствует.
  const myPriceParam = params.get('myPrice');
  const myPrice = !freeMode && myPriceParam != null && myPriceParam !== '' && Number.isFinite(Number(myPriceParam)) ? Number(myPriceParam) : undefined;
  const [agreeSupply, setAgreeSupply] = useState(false);
  const [agreeOferta, setAgreeOferta] = useState(false);
  const [comment, setComment] = useState('');
  const [byGost, setByGost] = useState(true);
  const [qaOverrides, setQaOverrides] = useState<QaOverrides>({});
  const setOverride = useCallback((name: string, field: 'inc' | 'dec', value: number | string) => {
    setQaOverrides((current) => ({ ...current, [name]: { ...(current[name] ?? { inc: '', dec: '' }), [field]: value } }));
  }, []);
  // Отмена поллинга при уходе со страницы.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const view = useQuery({
    queryKey: ['saleOrderView', saleOrderId, saleOrderVersion, fromPurchaseOrderId, toAddress] as const,
    queryFn: ({ signal }) =>
      orderService.getSaleOrderView(
        {
          orderId: saleOrderId,
          orderVersion: saleOrderVersion,
          fromPurchaseOrderId,
          toAddress: toAddress!,
          toAddressTitle: toAddressTitle!,
        },
        signal,
      ),
    enabled: isAuthenticated && valid,
    staleTime: 30_000,
    retry: false,
  });

  // ГОСТ-дефолты приёмки — для payload сделки и отображения (как старый портал).
  const qaDefaults = useQuery({
    queryKey: ['qaDefaults', goods] as const,
    queryFn: ({ signal }) => orderService.getQaDefaults(goods!, signal),
    enabled: isAuthenticated && !!goods,
    staleTime: 5 * 60_000,
  });

  // Свободный режим: префилл порогов приёмки из дефолтов свойств (один раз, при загрузке).
  useEffect(() => {
    if (freeMode && qaDefaults.data) {
      setQaOverrides((current) => (Object.keys(current).length ? current : initQaOverrides(qaDefaults.data)));
    }
  }, [freeMode, qaDefaults.data]);

  // Скачивание файлов продавца (декларация / особые условия) — read-only, по fileId из view-ответа.
  const downloadFile = useMutation({
    mutationFn: (fileId: number) => orderService.downloadOrderFile(fileId),
    onError: (error) =>
      notifications.show({
        color: 'red',
        message: isServiceUnavailable(error) ? t('auth.errors.unavailable') : (getApiMessage(error) ?? t('auth.errors.generic')),
      }),
  });

  const buy = useMutation({
    mutationFn: async (): Promise<number> => {
      const processId = await orderService.createDealFromSale({
        orderId: saleOrderId,
        orderVersion: view.data?.order.version ?? saleOrderVersion,
        creationSection: 'BUY_CPT_BEST_PRICE',
        isBestPrice: true,
        userComment: comment.trim(),
        fromPurchaseOrderId,
        // Свободный режим: приёмку задаёт покупатель (ГОСТ/пороги). С прайсом — ГОСТ по умолчанию
        // (byGost=true, без переопределений → как было; реальную приёмку резолвит бэкенд из заявки).
        qualityAcceptance: buildQualityAcceptance(byGost, qaOverrides, qaDefaults.data ?? []),
        partnerAddress: toAddress!,
        partnerAddressTitle: toAddressTitle!,
      });
      const deadline = Date.now() + POLL_DEADLINE_MS;
      while (Date.now() < deadline && mountedRef.current) {
        await sleep(POLL_INTERVAL_MS);
        const status = await orderService.getBargainCreateStatus(processId);
        if (status.created && status.bargainId != null) {
          return status.bargainId;
        }
        if (status.suspended) {
          throw new DealSuspendedError();
        }
      }
      throw new DealTimeoutError();
    },
    onSuccess: (bargainId) => {
      notifications.show({ color: 'lime', message: t('app.buy.offer.dealCreated', { id: bargainId }) });
      navigate(`${ROUTES.APP_DEALS}/${bargainId}`);
    },
    onError: (error) => {
      if (!mountedRef.current) {
        return;
      }
      if (error instanceof DealTimeoutError) {
        notifications.show({ color: 'wheat', message: t('app.buy.offer.dealTimeout') });
        navigate(ROUTES.APP_DEALS);
        return;
      }
      if (error instanceof DealSuspendedError) {
        notifications.show({ color: 'red', message: t('app.buy.offer.dealSuspended') });
        return;
      }
      const status = getStatus(error);
      if (status === 410 || status === 424) {
        notifications.show({ color: 'red', message: t('app.buy.offer.gone') });
        return;
      }
      if (status === 412) {
        view.refetch();
        notifications.show({ color: 'wheat', message: t('app.buy.offer.changed') });
        return;
      }
      if (status === 422) {
        // Бэкенд сообщает, каких реквизитов не хватает (requiredFields в конверте).
        const envelope = error instanceof ApiError ? error.envelope : undefined;
        const fields = (envelope?.data as { requiredFields?: string[] } | undefined)?.requiredFields;
        notifications.show({
          color: 'red',
          message: fields?.length
            ? `${t('app.buy.offer.missingData')}: ${fields.join(', ')}`
            : t('app.buy.offer.missingData'),
        });
        return;
      }
      if (status === 423) {
        notifications.show({ color: 'wheat', message: t('app.banner.certification') });
        navigate(ROUTES.APP_COMPANY_ACCREDITATION);
        return;
      }
      notifications.show({
        color: 'red',
        message: isServiceUnavailable(error)
          ? t('auth.errors.unavailable')
          : (getApiMessage(error) ?? t('auth.errors.generic')),
      });
    },
  });

  // Битая глубокая ссылка → назад в список.
  // Агент не заключает сделок как сторона — он посредник. Заключение сделки покупателем (в т.ч. прямой
  // заход по URL) агенту недоступно: возвращаем к списку «Купить».
  if (isAgent) {
    return <Navigate to={ROUTES.APP_BUY} replace />;
  }

  if (!valid) {
    return <Navigate to={ROUTES.APP_BUY} replace />;
  }

  // С прайса ведём назад к списку «Купить» (страница одного базиса убрана); со свободного поиска —
  // на экран «Лучшая цена».
  const backUrl = fromPurchaseOrderId != null ? ROUTES.APP_BUY : ROUTES.APP_BEST_PRICE;
  const backLabel = fromPurchaseOrderId != null ? t('app.buy.offer.back') : t('app.buy.offer.backSearch');

  const viewStatus = getStatus(view.error);
  const offerGone = viewStatus === 410 || viewStatus === 424;
  const data = view.data;
  const order = data?.order;
  const price = data?.priceInfo?.cptPricePerTonWithoutNds;
  const total = data?.priceInfo?.fullPriceCPTWithoutNds;
  // Сводка сделки: тоннаж — из оффера продавца; качество — первое свойство культуры (протеин).
  const mainQuality = order?.goodsPropertiesSet?.properties?.[0];
  const acceptance = data?.contragentQualityAcceptance?.qaProperties ?? qaDefaults.data ?? [];
  const qaProps = qaDefaults.data ?? [];
  // Свободный режим: «Купить» доступна только при заполненной приёмке (ГОСТ или все пороги).
  const acceptanceReady = !freeMode || isAcceptanceComplete(byGost, qaOverrides, qaProps);
  const creditKey = order?.qualityCreditType ? CREDIT_TYPE_KEYS[order.qualityCreditType] : undefined;
  // Цена обязательна: без рассчитанной CPT-цены оформлять сделку нельзя (симметрично canSell на sell-стороне).
  const canBuy = agreeSupply && agreeOferta && !buy.isPending && !!order && acceptanceReady && price != null;

  // Чего не хватает для сделки — единая красная сводка (как на формах создания). Цену/загрузку не включаем.
  const dealMissing = [
    !acceptanceReady ? 'app.buy.form.needQa' : null,
    !agreeSupply || !agreeOferta ? 'app.buy.offer.needAgree' : null,
  ].filter((key): key is string => key !== null);

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Stack gap="xs">
          <Anchor component={Link} to={backUrl} size="sm" c="brand.7">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} />
              {backLabel}
            </Group>
          </Anchor>
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon variant="light" color="brand" size={44} radius="md" style={{ flexShrink: 0 }}>
              <IconWheat size={26} />
            </ThemeIcon>
            <Stack gap={4} style={{ minWidth: 0 }}>
              <Title order={2}>{order?.goodsType ?? t('app.buy.offer.title')}</Title>
              {order?.userInfo?.name ? (
                <Group gap={6} wrap="nowrap">
                  <IconBuilding size={16} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
                  <Text size="sm" c="gray.7">
                    {t('app.buy.offer.seller')}:
                  </Text>
                  <Text fw={500}>{order.userInfo.name}</Text>
                  {order.userInfo.inn ? (
                    <Text size="sm" c="gray.7">
                      · {t('auth.fields.inn')} <IdText>{order.userInfo.inn}</IdText>
                    </Text>
                  ) : null}
                </Group>
              ) : null}
            </Stack>
          </Group>
        </Stack>

        {view.isError ? (
          <Alert
            color={offerGone ? 'wheat' : 'red'}
            variant="light"
            icon={<IconAlertTriangle size={18} />}
          >
            <Group justify="space-between" wrap="nowrap">
              <Text>
                {offerGone
                  ? t('app.buy.offer.gone')
                  : isServiceUnavailable(view.error)
                    ? t('auth.errors.unavailable')
                    : t('auth.errors.generic')}
              </Text>
              {offerGone ? (
                <Button component={Link} to={backUrl} variant="subtle" size="compact-sm">
                  {backLabel}
                </Button>
              ) : (
                <Button variant="subtle" size="compact-sm" onClick={() => view.refetch()}>
                  {t('common.retry')}
                </Button>
              )}
            </Group>
          </Alert>
        ) : view.isLoading || !data ? (
          <Stack gap="sm">
            <Skeleton h={120} />
            <Skeleton h={220} />
          </Stack>
        ) : (
          <>
            {/* Сводка сделки (Вариант A): товар/качество/тоннаж → контраст «моя → лучшая (CPT)» + выгода → итоги. */}
            <DealSummaryCard
              goods={order?.goodsType ?? goods ?? t('app.buy.offer.title')}
              quality={mainQuality}
              weightTon={order?.weightTon}
              action="buy"
              bestPrice={price}
              bestPriceLabel={t('app.buy.offer.pricePerTon')}
              myPrice={myPrice}
              advantage={advantage}
              prepaymentValue={order?.prepaymentInfo?.percent}
              total={total}
              totalLabel={t('app.buy.offer.total')}
              distance={data.distance}
              distanceLabel={t('app.buy.offer.distance')}
            />

            {/* Маршрут: погрузка у продавца → выгрузка на моём базисе. */}
            <Card withBorder padding="lg">
              <Group gap="sm" wrap="nowrap" align="flex-start">
                <ThemeIcon variant="light" color="brand" size={32} radius="md" style={{ flexShrink: 0 }}>
                  <IconMapPin size={18} />
                </ThemeIcon>
                <Stack gap={6} style={{ minWidth: 0 }}>
                  <Text fw={600} size="sm">
                    {t('app.buy.offer.route')}
                  </Text>
                  <Group gap={8} wrap="wrap">
                    <Text size="sm" c="gray.8">
                      {formatOrgName(data.fromAddress?.title ?? order?.addressTitle) ?? order?.address ?? '—'}
                    </Text>
                    <IconArrowRight size={14} color="var(--mantine-color-brand-6)" />
                    <Text size="sm" c="gray.8">
                      {formatOrgName(toAddressTitle)}
                    </Text>
                  </Group>
                </Stack>
              </Group>
            </Card>

            <Stack gap="sm">
              <SectionLabel>{t('app.buy.offer.termsTitle')}</SectionLabel>
              <Card withBorder padding="lg">
                <Stack gap="md">
                  {/* Приёмка — это МОЁ условие (покупателя), вынесена ниже в «Ваши условия приёмки».
                      Здесь, в «Условия продавца», — только условия продавца (зачёт качества/погрузка/…). */}
                  {creditKey ? (
                    <TermRow icon={<IconScale size={18} />} label={t('app.buy.terms.credit')}>
                      <Text size="sm" c="gray.8">
                        {t(creditKey)}
                      </Text>
                    </TermRow>
                  ) : null}

                  {order?.prepaymentInfo?.name || order?.prepaymentInfo?.percent != null ? (
                    <TermRow icon={<IconCoin size={18} />} label={t('app.buy.offer.prepayment')}>
                      <Text size="sm" c="gray.8">
                        {order.prepaymentInfo.name ??
                          (order.prepaymentInfo.percent === 0
                            ? // Тип предоплаты, не заголовок цены — короткая подпись без «CPT…, ₽/т».
                              t('app.buy.prepayment0Short')
                            : `${order.prepaymentInfo.percent}%`)}
                      </Text>
                    </TermRow>
                  ) : null}

                  {/* Условия продавца read-only (как старый портал preview-offer-for-sale): погрузка → хранение
                      → декларация. Данные из /order/sale/by_id; в свободном режиме их неоткуда взять иначе.
                      Общий вид с превью сделки продавца — через SellerCoreTermsView. */}
                  {order ? (
                    <SellerCoreTermsView
                      order={order}
                      onDownload={(id) => downloadFile.mutate(id)}
                      downloadingId={downloadFile.isPending ? downloadFile.variables : undefined}
                    />
                  ) : null}

                  {order?.specialTermsFiles?.length ? (
                    <TermRow icon={<IconFileText size={18} />} label={t('app.buy.offer.specialTerms')}>
                      <Stack gap={4}>
                        <Text size="sm" c="gray.8">
                          {t('app.buy.offer.specialTermsNote')}
                        </Text>
                        <OrderFileLinks
                          files={order.specialTermsFiles}
                          onDownload={(id) => downloadFile.mutate(id)}
                          downloadingId={downloadFile.isPending ? downloadFile.variables : undefined}
                        />
                      </Stack>
                    </TermRow>
                  ) : null}

                  {order?.userComment ? (
                    <TermRow icon={<IconInfoCircle size={18} />} label={t('app.buy.terms.comment')}>
                      <Text size="sm" c="gray.8" style={{ whiteSpace: 'pre-wrap' }}>
                        {order.userComment}
                      </Text>
                    </TermRow>
                  ) : null}
                </Stack>
              </Card>
            </Stack>

            {/* Мои условия приёмки (непересекающееся условие покупателя): свободный режим — редактор;
                привязанный — read-only приёмка из моего прайса (contragentQualityAcceptance = моя приёмка). */}
            <Stack gap="sm">
              <SectionLabel>{t('app.buy.offer.myAcceptance')}</SectionLabel>
              <Card withBorder padding="lg">
                {freeMode ? (
                  <BuyAcceptanceFields
                    qaProps={qaProps}
                    byGost={byGost}
                    onByGost={setByGost}
                    overrides={qaOverrides}
                    onOverride={setOverride}
                    loading={qaDefaults.isLoading}
                    noProps={qaDefaults.isSuccess && qaProps.length === 0}
                  />
                ) : (
                  <TermRow icon={<IconClipboardCheck size={18} />} label={t('app.buy.terms.acceptance')}>
                    <Stack gap={6}>
                      <Text size="sm" c="gray.8">
                        {t('app.buy.terms.acceptanceGost')}
                      </Text>
                      {acceptance.length > 0 ? (
                        <Group gap={4}>
                          {acceptance.map((property) => (
                            <Badge key={property.name} variant="light" color="gray" tt="none" fw={500} size="lg" radius="sm">
                              {property.name}
                              {property.gostValue ? ` ${decodeEntities(property.gostValue)}` : ''}
                            </Badge>
                          ))}
                        </Group>
                      ) : null}
                    </Stack>
                  </TermRow>
                )}
              </Card>
            </Stack>

            <Textarea
              label={t('app.buy.offer.commentLabel')}
              placeholder={t('app.buy.offer.commentPlaceholder')}
              maxLength={4096}
              autosize
              minRows={2}
              value={comment}
              onChange={(event) => setComment(event.currentTarget.value)}
            />

            <Stack gap="sm">
              <Checkbox
                checked={agreeSupply}
                onChange={(event) => setAgreeSupply(event.currentTarget.checked)}
                label={
                  <Text size="sm">
                    {t('app.buy.offer.agreeSupplyPrefix')}{' '}
                    <Anchor
                      href={ROUTES.SUPPLY}
                      target="_blank"
                      size="sm"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {t('app.buy.offer.agreeSupplyLink')}
                    </Anchor>
                    {t('app.buy.offer.agreeSupplySuffix')}
                  </Text>
                }
              />
              <Checkbox
                checked={agreeOferta}
                onChange={(event) => setAgreeOferta(event.currentTarget.checked)}
                label={<Text size="sm">{t('app.buy.offer.agreeOferta')}</Text>}
              />
            </Stack>

            <FormErrorsSummary title={t('common.fixFormErrors')} items={dealMissing.map((key) => t(key))} />

            <Group justify="flex-end" gap="sm">
              <Button component={Link} to={backUrl} variant="default" disabled={buy.isPending}>
                {t('common.cancel')}
              </Button>
              <Button onClick={() => buy.mutate()} loading={buy.isPending} disabled={!canBuy}>
                {buy.isPending ? t('app.buy.offer.buying') : t('app.buy.offer.buy')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Container>
  );
}
